/**
 * ManageCompetitorsModal — Multi-select picker for peer companies.
 *
 * Lists EVERY company in the database (not just same-sector peers) with a
 * checkbox; same-sector companies are flagged and sorted to the top. The user
 * toggles selections locally, then clicks Apply to persist the resulting diff
 * in one batch.
 *
 * The list is fetched once on open and filtered client-side (deferred), so
 * typing never fires a request and never resizes the dialog — the scroll region
 * is a fixed height and the dialog is top-anchored, which removes the "bounce"
 * the old sector-only + per-keystroke-network-search layout had.
 *
 * UI conventions follow the rest of the app (see AddSubdomainModal).
 */
import { useEffect, useMemo, useState, useDeferredValue } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  X,
  AlertCircle,
  Loader2,
  Search,
  CheckCircle2,
  Building2,
  Users,
} from 'lucide-react';
import LoadingCard from '../ui/LoadingCard';
import { useSectorPeers } from './hooks/useCompetitors';
import {
  addCompanyCompetitor,
  removeCompanyCompetitor,
  type SectorPeer,
} from '../../lib/api/financialApi';
import { standaloneCompaniesAPI } from '../../lib/api';
import { useFinancialStore } from '../../stores/financialStore';

interface ManageCompetitorsModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyName: string;
  peers: string[];
}

interface PickerItem {
  key: string; // lower-cased name — the dedupe/identity key
  name: string; // canonical display name
  ticker?: string | null;
  industry?: string | null;
  country?: string | null;
  yearsCount?: number | null;
  isSameSector: boolean;
}

/** Map a company's `source` to a display region, mirroring CompanyResearch. */
function regionLabel(source?: string | null, fallback?: string | null): string | null {
  const s = (source ?? '').toLowerCase();
  if (s === 'india' || s === 'moneycontrol' || s === 'bse' || s === 'nse') return 'India';
  if (s === 'uk' || s === 'companies_house') return 'UK';
  if (s === 'sec' || s === 'sec_edgar') return 'US';
  return fallback ?? null;
}

export default function ManageCompetitorsModal({
  isOpen,
  onClose,
  companyName,
  peers,
}: ManageCompetitorsModalProps) {
  const queryClient = useQueryClient();
  const setCompetitorsList = useFinancialStore((s) => s.setCompetitorsList);

  // Full company list — the primary source. Shares the ['companies'] cache with
  // the Company Research page, so it's usually instant.
  const allCompaniesQuery = useQuery({
    queryKey: ['companies'],
    queryFn: () => standaloneCompaniesAPI.listCompanies() as Promise<any[]>,
    enabled: isOpen,
    staleTime: 1000 * 60 * 5,
  });

  // Sector peers — best-effort enrichment only: flags same-sector companies and
  // supplies industry/years-of-data. Failures don't block the picker.
  const sectorPeersQuery = useSectorPeers(companyName, isOpen, 100);

  const [selected, setSelected] = useState<Set<string>>(new Set(peers));
  const [filter, setFilter] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reset selection + filter whenever the modal reopens or peers change.
  useEffect(() => {
    if (isOpen) {
      setSelected(new Set(peers));
      setFilter('');
      setSubmitError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, peers.join('|')]);

  // Escape to close, lock body scroll.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen, onClose]);

  const sectorPeers: SectorPeer[] = sectorPeersQuery.data?.peers ?? [];
  const sectorName = sectorPeers[0]?.industry ?? null;

  const sectorNameSet = useMemo(
    () => new Set(sectorPeers.map((p) => p.name.toLowerCase())),
    [sectorPeers],
  );

  const peerSetLower = useMemo(
    () => new Set(peers.map((p) => p.toLowerCase())),
    [peers],
  );

  // Merge every source into one deduped, sorted list: all DB companies, enriched
  // with sector-peer metadata, plus any current peer not otherwise present.
  // Same-sector companies sort first; the rest alphabetically. The focal company
  // is always excluded.
  const items = useMemo<PickerItem[]>(() => {
    const byLower = new Map<string, PickerItem>();
    const focalLower = companyName.toLowerCase();

    // Seed from sector peers first — these may include stubs not returned by
    // /companies, and carry industry + years-of-data.
    for (const p of sectorPeers) {
      const lower = p.name.toLowerCase();
      if (!lower || lower === focalLower) continue;
      byLower.set(lower, {
        key: lower,
        name: p.name,
        ticker: null,
        industry: p.industry ?? null,
        country: p.country ?? null,
        yearsCount: p.years_count ?? null,
        isSameSector: true,
      });
    }

    // Overlay the full company list.
    const all = Array.isArray(allCompaniesQuery.data) ? allCompaniesQuery.data : [];
    for (const c of all) {
      const name = String(c?.companyName ?? c?.name ?? '');
      const lower = name.toLowerCase();
      if (!lower || lower === focalLower) continue;
      const existing = byLower.get(lower);
      byLower.set(lower, {
        key: lower,
        name: existing?.name ?? name,
        ticker: c?.ticker ?? existing?.ticker ?? null,
        industry: existing?.industry ?? null,
        country: regionLabel(c?.source, existing?.country ?? null),
        yearsCount: existing?.yearsCount ?? null,
        isSameSector: existing?.isSameSector ?? sectorNameSet.has(lower),
      });
    }

    // Any current peer not surfaced by either source — keep it visible/selectable.
    for (const name of peers) {
      const lower = name.toLowerCase();
      if (!lower || lower === focalLower || byLower.has(lower)) continue;
      byLower.set(lower, {
        key: lower,
        name,
        ticker: null,
        industry: null,
        country: null,
        yearsCount: null,
        isSameSector: sectorNameSet.has(lower),
      });
    }

    const arr = [...byLower.values()];
    arr.sort((a, b) => {
      if (a.isSameSector !== b.isSameSector) return a.isSameSector ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
    return arr;
  }, [sectorPeers, allCompaniesQuery.data, sectorNameSet, companyName, peers]);

  // Deferred so typing stays responsive and the list re-filters off the main
  // input path — no network, no layout thrash.
  const deferredFilter = useDeferredValue(filter);
  const filteredItems = useMemo(() => {
    const q = deferredFilter.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (it) =>
        it.name.toLowerCase().includes(q) ||
        (it.ticker ?? '').toLowerCase().includes(q) ||
        (it.industry ?? '').toLowerCase().includes(q),
    );
  }, [items, deferredFilter]);

  const toggle = (name: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      filteredItems.forEach((p) => next.add(p.name));
      return next;
    });
  };

  const clearAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      filteredItems.forEach((p) => next.delete(p.name));
      return next;
    });
  };

  // Diff between original peers and current local selection.
  const { toAdd, toRemove } = useMemo(() => {
    const originalLower = peerSetLower;
    const selectedLower = new Set([...selected].map((n) => n.toLowerCase()));

    const add: string[] = [];
    const remove: string[] = [];

    for (const name of selected) {
      if (!originalLower.has(name.toLowerCase())) add.push(name);
    }
    for (const name of peers) {
      if (!selectedLower.has(name.toLowerCase())) remove.push(name);
    }
    return { toAdd: add, toRemove: remove };
  }, [selected, peers, peerSetLower]);

  const hasChanges = toAdd.length > 0 || toRemove.length > 0;

  const applyMutation = useMutation({
    mutationFn: async () => {
      // Run adds and removes in parallel. The last API response carries the
      // canonical updated list, but we re-fetch via invalidation anyway.
      await Promise.all([
        ...toAdd.map((name) => addCompanyCompetitor(companyName, name)),
        ...toRemove.map((name) => removeCompanyCompetitor(companyName, name)),
      ]);
    },
    onSuccess: () => {
      // Pre-set the store from local selection so charts react before the
      // refetch resolves. The query invalidation will reconcile shortly.
      setCompetitorsList([...selected]);
      queryClient.invalidateQueries({
        queryKey: ['financial', 'competitors', companyName],
      });
      onClose();
    },
    onError: (err: any) => {
      const detail =
        err?.response?.data?.detail ?? err?.message ?? 'Failed to apply changes';
      setSubmitError(detail);
    },
  });

  const handleApply = () => {
    if (!hasChanges || applyMutation.isPending) return;
    setSubmitError(null);
    applyMutation.mutate();
  };

  if (!isOpen) return null;

  const loading = allCompaniesQuery.isLoading;
  const loadError = allCompaniesQuery.error as Error | null;
  const totalSelectedVisible = filteredItems.filter((p) =>
    selected.has(p.name),
  ).length;

  return (
    // items-start (not center) + padding: the dialog is top-anchored, so it can
    // never re-center — a big contributor to the old "bounce".
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black bg-opacity-50 p-4 py-[6vh]">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full flex flex-col max-h-[88vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200 shrink-0">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
              <Users className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-neutral-900">
                Manage Competitors
              </h2>
              <p className="text-sm text-neutral-600 mt-1">
                Select peers for{' '}
                <span className="font-semibold">{companyName}</span>
                {sectorName ? (
                  <>
                    {' '}
                    — companies in <span className="font-semibold">{sectorName}</span> are
                    listed first
                  </>
                ) : null}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
            disabled={applyMutation.isPending}
            aria-label="Close"
          >
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {/* Filter input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search all companies by name, ticker or sector…"
              className="w-full pl-10 pr-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={applyMutation.isPending}
            />
          </div>

          {/* List header */}
          <div className="flex items-center justify-between">
            <label className="block text-sm font-semibold text-neutral-900">
              All companies
              {!loading && (
                <span className="ml-2 text-xs font-normal text-neutral-500">
                  {totalSelectedVisible} of {filteredItems.length} selected
                </span>
              )}
            </label>
            {!loading && filteredItems.length > 0 && (
              <div className="flex items-center gap-2 text-xs">
                <button
                  type="button"
                  onClick={selectAllVisible}
                  disabled={applyMutation.isPending}
                  className="text-primary-700 hover:text-primary-800 font-medium disabled:opacity-40"
                >
                  Select all
                </button>
                <span className="text-neutral-300">·</span>
                <button
                  type="button"
                  onClick={clearAllVisible}
                  disabled={applyMutation.isPending}
                  className="text-neutral-600 hover:text-neutral-800 font-medium disabled:opacity-40"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>

          {/* Fixed-height scroll region — filtering changes only what's inside
              here, never the region's height, so the dialog stays put. */}
          <div className="h-[46vh] overflow-y-auto border border-neutral-200 rounded-lg">
            {loading ? (
              <div className="p-3">
                <LoadingCard asCard={false} message="Loading companies…" height={160} delayMs={0} />
              </div>
            ) : loadError ? (
              <div className="m-3 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-900">Failed to load companies</p>
                  <p className="text-sm text-red-700 mt-1">{loadError.message}</p>
                </div>
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center px-6 text-center">
                <Building2 className="w-6 h-6 text-neutral-300 mb-2" />
                <p className="text-sm text-neutral-500">
                  {filter
                    ? `No companies match "${filter}".`
                    : 'No companies in the database yet.'}
                </p>
                {filter && (
                  <p className="text-xs text-neutral-400 mt-2">
                    If the company isn't here yet, research it first on the Company Research page.
                  </p>
                )}
              </div>
            ) : (
              <ul className="divide-y divide-neutral-100">
                {filteredItems.map((p) => {
                  const checked = selected.has(p.name);
                  const wasAlreadyPeer = peerSetLower.has(p.key);
                  return (
                    <li key={p.key}>
                      <label
                        className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-neutral-50 ${
                          checked ? 'bg-primary-50/40' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(p.name)}
                          disabled={applyMutation.isPending}
                          className="w-4 h-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
                        />
                        <div className="w-8 h-8 rounded-md bg-neutral-100 flex items-center justify-center shrink-0">
                          <Building2 className="w-4 h-4 text-neutral-400" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-neutral-900 truncate">
                              {p.name}
                            </span>
                            {p.ticker && (
                              <span className="text-xs text-neutral-500">({p.ticker})</span>
                            )}
                            {p.isSameSector && (
                              <span className="inline-flex items-center text-[10px] font-medium text-violet-700 px-1.5 py-0.5 rounded-full bg-violet-50 border border-violet-100">
                                Same sector
                              </span>
                            )}
                            {wasAlreadyPeer && (
                              <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-emerald-700 px-1.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-100">
                                <CheckCircle2 className="w-2.5 h-2.5" />
                                Current
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-neutral-500 mt-0.5 flex flex-wrap items-center gap-x-1.5">
                            {p.industry && <span>{p.industry}</span>}
                            {p.industry && p.country && <span className="text-neutral-300">·</span>}
                            {p.country && <span>{p.country}</span>}
                            {p.yearsCount != null && (p.industry || p.country) && (
                              <span className="text-neutral-300">·</span>
                            )}
                            {p.yearsCount != null && (
                              <span>
                                {p.yearsCount} {p.yearsCount === 1 ? 'year' : 'years'} of data
                              </span>
                            )}
                          </div>
                        </div>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Submit error */}
          {submitError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-red-900">Failed to apply changes</p>
                <p className="text-sm text-red-700 mt-1">{submitError}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-neutral-200 bg-neutral-50 shrink-0">
          <div className="text-sm text-neutral-600">
            {hasChanges ? (
              <span className="font-medium text-neutral-800">
                {toAdd.length > 0 && (
                  <span className="text-emerald-700">{toAdd.length} to add</span>
                )}
                {toAdd.length > 0 && toRemove.length > 0 && (
                  <span className="text-neutral-400"> · </span>
                )}
                {toRemove.length > 0 && (
                  <span className="text-red-700">{toRemove.length} to remove</span>
                )}
              </span>
            ) : (
              <span className="text-neutral-500">No changes</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
              disabled={applyMutation.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={!hasChanges || applyMutation.isPending}
              className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {applyMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Applying…
                </>
              ) : (
                <>Apply changes</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
